import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, X, Filter, Trash2, Loader2, Search, User as UserIcon, AlertCircle, CheckCircle2, Wand2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  getOperators, DATE_PRESETS, ROLLING_KINDS, SCOPES, ME_TOKEN, displayLabel,
} from '../engine/reportEngine';
import { metadataApi } from '../api/metadataApi';

/**
 * FilterBuilder 2.0 — numbered conditions with three-tier logic.
 *
 *   Scope       "Show me: My / My team's / All records" (Salesforce's
 *               standard filter; enforced server-side with sharing rules).
 *   Conditions  Flat, numbered list. Type-aware value editors: relative
 *               dates (presets + rolling N), multi-select picklist chips,
 *               tag operators, and typeahead pickers for lookup/user
 *               fields with a dynamic "Me (current user)" token.
 *   Logic       ALL / ANY / Advanced — Advanced exposes the expression
 *               ("1 AND (2 OR 3)") with live server-side validation.
 *
 * Filter tree shape stays backward-compatible:
 *   { type:'group', logic:'AND'|'OR'|'CUSTOM', expression?, children:[…] }
 * Legacy reports with nested sub-groups render a one-click "convert"
 * banner (flattens with AND semantics) before editing.
 */

const uid = (p = 'c') => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const USER_FIELD_KEYS = new Set(['owner', 'owner_id', 'created_by', 'assigned_to', 'last_modified_by']);

// Effective editor type for a field — owner/creator fields behave as
// user pickers even when the metadata types them as text.
const editorType = (field) => {
  if (!field) return 'text';
  const rawKey = (field.raw_key || field.key || '').split('.').pop();
  if (USER_FIELD_KEYS.has(rawKey)) return 'user';
  return field.type || 'text';
};

const makeCondition = (field) => {
  const ops = getOperators(editorType(field));
  const op = ops[0];
  let value = '';
  if (op.preset) value = 'this_month';
  else if (op.boolean) value = 'true';
  else if (op.multi) value = [];
  return {
    id: uid('c'), type: 'condition', field: field.key, operator: op.value, value, valueLabels: {},
  };
};

const hasNestedGroups = (node) => (node?.children || []).some((c) => c.type === 'group');

const flattenLegacy = (node) => {
  const out = [];
  const walk = (n) => (n.children || []).forEach((c) => (c.type === 'group' ? walk(c) : out.push(c)));
  walk(node);
  return out;
};

// ───────────────────────── value editors ─────────────────────────

const DateValueEditor = ({ condition, onChange }) => {
  const v = String(condition.value || 'this_month');
  const isRolling = v.includes(':');
  const [kind, nRaw] = isRolling ? v.split(':') : ['last_n_days', '7'];

  return (
    <div className="space-y-1.5">
      <select
        value={isRolling ? '__rolling__' : v}
        onChange={(e) => {
          const nv = e.target.value;
          onChange(nv === '__rolling__' ? 'last_n_days:7' : nv);
        }}
        className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white w-full"
      >
        {DATE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        <option value="__rolling__">Custom rolling window…</option>
      </select>
      {isRolling && (
        <div className="flex items-center gap-1.5">
          <select
            value={kind}
            onChange={(e) => onChange(`${e.target.value}:${nRaw}`)}
            className="h-8 text-xs border border-slate-200 rounded-md px-1.5 bg-white flex-1 min-w-0"
          >
            {ROLLING_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <Input
            type="number" min="0" value={nRaw}
            onChange={(e) => onChange(`${kind}:${Math.max(0, parseInt(e.target.value || '0', 10))}`)}
            className="h-8 text-xs w-16 flex-shrink-0" placeholder="N"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Typeahead multi-picker for lookup / user fields. Selected entries are
 * stored as ids in condition.value; labels cached in condition.valueLabels
 * so chips render without re-fetching.
 */
const LookupValueEditor = ({ field, condition, onChange, mode }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  const selected = Array.isArray(condition.value) ? condition.value : [];
  const labels = condition.valueLabels || {};

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const target = field.lookup_object || field.reference_to;
        const results = mode === 'user'
          ? await metadataApi.listUsers(query)
          : await metadataApi.searchRecords(target, query);
        if (!cancelled) setOptions(results || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, open, mode, field.lookup_object, field.reference_to]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const add = (id, label) => {
    if (selected.includes(id)) return;
    onChange([...selected, id], { ...labels, [id]: label });
    setQuery('');
  };
  const remove = (id) => {
    const next = selected.filter((x) => x !== id);
    const nl = { ...labels };
    delete nl[id];
    onChange(next, nl);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex flex-wrap gap-1 mb-1">
        {selected.map((id) => (
          <span key={id} className={`inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded-full border ${
            id === ME_TOKEN ? 'bg-violet-50 border-violet-200 text-violet-700' : 'bg-indigo-50 border-indigo-200 text-indigo-700'
          }`}>
            {id === ME_TOKEN && <UserIcon className="h-2.5 w-2.5" />}
            {id === ME_TOKEN ? 'Me (current user)' : (labels[id] || id)}
            <button type="button" onClick={() => remove(id)} className="hover:bg-white/60 rounded-full p-0.5">
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
        <Input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={mode === 'user' ? 'Search users…' : 'Search records…'}
          className="h-8 text-xs pl-7"
        />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-44 overflow-y-auto">
          {mode === 'user' && !selected.includes(ME_TOKEN) && (
            <button type="button" onClick={() => add(ME_TOKEN, 'Me (current user)')}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-violet-700 hover:bg-violet-50 text-left font-medium">
              <UserIcon className="h-3 w-3" /> Me (current user)
            </button>
          )}
          {loading && (
            <div className="px-2.5 py-2 text-xs text-slate-400 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> Searching…
            </div>
          )}
          {!loading && options.filter((o) => !selected.includes(o.id)).map((o) => (
            <button key={o.id} type="button" onClick={() => add(o.id, o.label)}
              className="w-full px-2.5 py-1.5 text-xs text-slate-700 hover:bg-indigo-50 text-left truncate">
              {o.label}
            </button>
          ))}
          {!loading && options.length === 0 && (
            <div className="px-2.5 py-2 text-xs text-slate-400">No matches</div>
          )}
        </div>
      )}
    </div>
  );
};

const ValueEditor = ({ field, condition, onChange, onChangeWithLabels }) => {
  const etype = editorType(field);
  const ops = getOperators(etype);
  const opMeta = ops.find((o) => o.value === condition.operator) || ops[0];
  if (opMeta.valueless) return null;

  if (etype === 'date') {
    if (opMeta.preset) return <DateValueEditor condition={condition} onChange={onChange} />;
    if (condition.operator === 'between') {
      const v = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <div className="flex gap-1">
          <Input type="date" value={v[0] || ''} onChange={(e) => onChange([e.target.value, v[1]])} className="h-8 text-xs" />
          <Input type="date" value={v[1] || ''} onChange={(e) => onChange([v[0], e.target.value])} className="h-8 text-xs" />
        </div>
      );
    }
    return <Input type="date" value={condition.value || ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" />;
  }

  if (etype === 'checkbox') {
    return (
      <div className="flex rounded-md overflow-hidden border border-slate-200 w-fit">
        {[['true', 'Yes'], ['false', 'No']].map(([v, label]) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`px-3 py-1 text-xs font-semibold transition-colors ${
              String(condition.value) === v ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}>
            {label}
          </button>
        ))}
      </div>
    );
  }

  if (['number', 'currency', 'percent'].includes(etype)) {
    if (condition.operator === 'between') {
      const v = Array.isArray(condition.value) ? condition.value : ['', ''];
      return (
        <div className="flex gap-1 items-center">
          <Input type="number" placeholder="min" value={v[0] ?? ''} onChange={(e) => onChange([e.target.value, v[1]])} className="h-8 text-xs" />
          <span className="text-xs text-slate-400">–</span>
          <Input type="number" placeholder="max" value={v[1] ?? ''} onChange={(e) => onChange([v[0], e.target.value])} className="h-8 text-xs" />
        </div>
      );
    }
    return <Input type="number" value={condition.value ?? ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" placeholder="value" />;
  }

  if ((etype === 'user' || etype === 'lookup') && opMeta.multi) {
    return (
      <LookupValueEditor
        field={field} condition={condition} mode={etype === 'user' ? 'user' : 'record'}
        onChange={onChangeWithLabels}
      />
    );
  }

  // Picklist / tags with known values → chip multi-select.
  if (opMeta.multi && field.values && field.values.length) {
    const sel = Array.isArray(condition.value) ? condition.value : [];
    return (
      <div className="flex flex-wrap gap-1">
        {field.values.map((v) => {
          const on = sel.includes(v);
          return (
            <button key={v} type="button"
              onClick={() => onChange(on ? sel.filter((x) => x !== v) : [...sel, v])}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                on ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
              }`}>
              {v}
            </button>
          );
        })}
      </div>
    );
  }
  if (opMeta.multi) {
    // Multi op but no known picklist values → comma-separated entry.
    const sel = Array.isArray(condition.value) ? condition.value : [];
    return (
      <Input
        value={sel.join(', ')}
        onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
        className="h-8 text-xs" placeholder="value1, value2, …"
      />
    );
  }
  if ((etype === 'select') && field.values && field.values.length) {
    return (
      <select value={condition.value || ''} onChange={(e) => onChange(e.target.value)}
        className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white w-full">
        <option value="">Choose…</option>
        {field.values.map((v) => <option key={v} value={v}>{v}</option>)}
      </select>
    );
  }
  return <Input value={condition.value || ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" placeholder="value" />;
};

// ───────────────────── advanced logic editor ─────────────────────

const defaultExpression = (count, logic) => (
  Array.from({ length: count }, (_, i) => i + 1).join(logic === 'OR' ? ' OR ' : ' AND ')
);

const AdvancedLogicEditor = ({ expression, conditionCount, onChange }) => {
  const [state, setState] = useState({ checking: false, valid: true, error: null });

  useEffect(() => {
    if (!expression?.trim()) { setState({ checking: false, valid: false, error: 'Enter an expression, e.g. 1 AND (2 OR 3)' }); return undefined; }
    let cancelled = false;
    setState((s) => ({ ...s, checking: true }));
    const t = setTimeout(async () => {
      try {
        const res = await metadataApi.validateFilterLogic(expression, conditionCount);
        if (!cancelled) setState({ checking: false, valid: res.valid, error: res.error });
      } catch {
        if (!cancelled) setState({ checking: false, valid: true, error: null }); // network blip → don't block
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [expression, conditionCount]);

  return (
    <div className="space-y-1">
      <div className="relative">
        <Input
          value={expression || ''}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className={`h-8 text-xs font-mono pr-7 ${state.valid ? '' : 'border-red-300 focus-visible:ring-red-300'}`}
          placeholder="1 AND (2 OR 3)"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          {state.checking
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-300" />
            : state.valid
              ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              : <AlertCircle className="h-3.5 w-3.5 text-red-400" />}
        </span>
      </div>
      {!state.valid && state.error && (
        <p className="text-[11px] text-red-500">{state.error}</p>
      )}
      <p className="text-[11px] text-slate-400">
        Combine numbered filters with AND, OR, NOT and parentheses.
      </p>
    </div>
  );
};

// ───────────────────────── main component ─────────────────────────

const FilterBuilder = ({ object, filters, fieldMap, onChange, scope, onScopeChange }) => {
  const conditions = useMemo(
    () => (filters?.children || []).filter((c) => c.type !== 'group'),
    [filters],
  );
  const legacy = hasNestedGroups(filters);
  const logic = (filters?.logic || 'AND').toUpperCase();

  // Field list: prefer the multi-object fieldMap (namespaced keys +
  // qualified labels); fall back to single-object metadata.
  const fields = useMemo(() => {
    const fm = fieldMap && Object.keys(fieldMap).length ? Object.values(fieldMap) : (object?.fields || []);
    return [...fm].sort((a, b) => (displayLabel(a) || '').localeCompare(displayLabel(b) || ''));
  }, [fieldMap, object]);

  const patchTree = (partial) => onChange({ ...(filters || { id: uid('grp'), type: 'group', children: [] }), ...partial });

  const setLogicMode = (mode) => {
    if (mode === 'CUSTOM') {
      patchTree({ logic: 'CUSTOM', expression: filters?.expression || defaultExpression(conditions.length, logic) });
    } else {
      patchTree({ logic: mode, expression: null });
    }
  };

  const addCondition = () => {
    if (!fields.length) return;
    patchTree({ children: [...conditions, makeCondition(fields[0])] });
  };
  const updateCondition = (idx, next) => {
    const children = [...conditions];
    children[idx] = next;
    patchTree({ children });
  };
  const removeCondition = (idx) => {
    const children = conditions.filter((_, i) => i !== idx);
    let expression = filters?.expression;
    if (logic === 'CUSTOM') expression = defaultExpression(children.length, 'AND');
    patchTree({ children, expression });
  };

  const convertLegacy = () => {
    patchTree({ children: flattenLegacy(filters), logic: 'AND', expression: null });
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-3 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Filter className="h-4 w-4 text-indigo-600" /> Filters
          {conditions.length > 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 rounded-full">{conditions.length}</span>
          )}
        </h3>
        {conditions.length > 0 && (
          <button onClick={() => patchTree({ children: [], expression: null, logic: 'AND' })}
            className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1">
            <Trash2 className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Scope — Salesforce "Show Me" */}
        {onScopeChange && (
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Show me</div>
            <div className="flex rounded-lg overflow-hidden border border-slate-200">
              {SCOPES.map((s) => (
                <button key={s.value} type="button" onClick={() => onScopeChange(s.value)}
                  className={`flex-1 px-1 py-1.5 text-[11px] font-semibold transition-colors ${
                    (scope || 'all') === s.value ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {legacy ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
            <p className="text-xs text-amber-800 leading-relaxed">
              This report uses legacy nested filter groups. Convert to the new
              numbered filters to edit here — conditions are kept, combined with AND.
            </p>
            <button onClick={convertLegacy}
              className="flex items-center gap-1 text-xs font-medium text-amber-900 bg-white border border-amber-300 rounded-md px-2 py-1 hover:bg-amber-100">
              <Wand2 className="h-3 w-3" /> Convert filters
            </button>
          </div>
        ) : (
          <>
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Conditions</div>
              <div className="space-y-1.5">
                {conditions.map((cond, idx) => {
                  const field = fieldMap?.[cond.field] || fields.find((f) => f.key === cond.field) || fields[0] || {};
                  const ops = getOperators(editorType(field));
                  return (
                    <div key={cond.id} className="bg-white border border-slate-200 rounded-lg p-2 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <span className="w-5 h-5 rounded-md bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                          {idx + 1}
                        </span>
                        <select value={cond.field}
                          onChange={(e) => {
                            const nf = fieldMap?.[e.target.value] || fields.find((f) => f.key === e.target.value);
                            if (nf) updateCondition(idx, { ...makeCondition(nf), id: cond.id });
                          }}
                          className="h-8 text-xs border border-slate-200 rounded-md px-1.5 bg-white flex-1 min-w-0">
                          {fields.map((f) => (
                            <option key={f.key} value={f.key}>{displayLabel(f) || f.label || f.key}</option>
                          ))}
                        </select>
                        <select value={cond.operator}
                          onChange={(e) => {
                            const opMeta = ops.find((o) => o.value === e.target.value) || {};
                            let value = cond.value;
                            if (opMeta.multi && !Array.isArray(value)) value = [];
                            if (opMeta.preset) value = 'this_month';
                            if (opMeta.value === 'between') value = ['', ''];
                            if (opMeta.valueless) value = '';
                            updateCondition(idx, { ...cond, operator: e.target.value, value });
                          }}
                          className="h-8 text-xs border border-slate-200 rounded-md px-1.5 bg-white flex-shrink-0 max-w-[110px]">
                          {ops.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <button onClick={() => removeCondition(idx)} className="text-slate-300 hover:text-red-500 p-1 flex-shrink-0">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <ValueEditor
                        field={field} condition={cond}
                        onChange={(v) => updateCondition(idx, { ...cond, value: v })}
                        onChangeWithLabels={(v, valueLabels) => updateCondition(idx, { ...cond, value: v, valueLabels })}
                      />
                    </div>
                  );
                })}
              </div>
              <button onClick={addCondition}
                className="mt-2 flex items-center gap-1 text-xs text-indigo-600 hover:bg-indigo-50 px-2 py-1 rounded-md font-medium">
                <Plus className="h-3.5 w-3.5" /> Add filter
              </button>
            </div>

            {conditions.length >= 2 && (
              <div>
                <div className="text-xs font-semibold text-slate-400 uppercase mb-1.5">Filter logic</div>
                <div className="flex rounded-lg overflow-hidden border border-slate-200 mb-2">
                  {[['AND', 'Match ALL'], ['OR', 'Match ANY'], ['CUSTOM', 'Advanced']].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setLogicMode(v)}
                      className={`flex-1 px-1 py-1.5 text-[11px] font-semibold transition-colors ${
                        logic === v ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                      }`}>
                      {label}
                    </button>
                  ))}
                </div>
                {logic === 'CUSTOM' && (
                  <AdvancedLogicEditor
                    expression={filters?.expression}
                    conditionCount={conditions.length}
                    onChange={(expression) => patchTree({ expression })}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FilterBuilder;
